import {format, formatDistanceToNowStrict, parseISO} from "date-fns"

// ai-zendesk `dialog.game` value -> Hermes route code (src/containers/GameActions availableGames).
// funky_bay has no Hermes section, so it is only visible under "All games".
export const AI_GAME_TO_HERMES = {
    clockmaker: "cm",
    bermuda_adventure: "ba",
    solitaire: "sc",
    toh: "toh",
    cube_match: "mc",
    diamond_sort: "ds",
    funky_bay: null
}

export const AI_GAME_LABELS = {
    clockmaker: "Clockmaker",
    bermuda_adventure: "Bermuda Adventures",
    solitaire: "Solitaire Cruise",
    toh: "Train of Hope",
    cube_match: "Cube Match",
    diamond_sort: "Brilliant Sort",
    funky_bay: "Funky Bay"
}

export const ALL_GAMES = "__all__"

/** Hermes route code ("cm") -> ai-zendesk game key ("clockmaker"), or null when not mapped */
export const hermesGameToAiGame = (hermesGame) =>
    Object.keys(AI_GAME_TO_HERMES).find((key) => AI_GAME_TO_HERMES[key] === hermesGame) ?? null

export const gameLabel = (game) => (game ? AI_GAME_LABELS[game] ?? game : "unknown game")

export const zendeskTicketUrl = (ticketId) => `https://belkatechnologies.zendesk.com/agent/tickets/${ticketId}`

export const DIALOG_STATUS_COLOR = {
    ACTIVE: "success",
    ESCALATED: "warning",
    AGENT_OWNED: "default",
    SOLVED: "default",
    CLOSED: "default"
}

export const DIALOG_STATUS_LABEL = {
    ACTIVE: "bot active",
    ESCALATED: "escalated",
    AGENT_OWNED: "agent owned",
    SOLVED: "solved",
    CLOSED: "closed"
}

export const ANSWER_STATUS_COLOR = {
    PENDING_APPROVAL: "warning",
    APPROVED: "info",
    SENT: "success",
    REJECTED: "default",
    SUPERSEDED: "default",
    SEND_FAILED: "error",
    RECORDED: "default",
    APPLIED: "default"
}

export const ANSWER_STATUS_LABEL = {
    PENDING_APPROVAL: "awaiting approval",
    APPROVED: "approved, not sent",
    SENT: "sent to player",
    REJECTED: "rejected",
    SUPERSEDED: "outdated",
    SEND_FAILED: "send failed",
    RECORDED: "recorded",
    APPLIED: "applied"
}

/** answer.source -> who authored the text; TEMPLATE answers are fixed texts picked by ruleKey */
export const answerAuthor = (answer) => (answer?.source === "TEMPLATE" ? "Template" : answer?.source === "SHEETS" ? "Sheets rule" : "AI")

export const RULE_KEY_LABEL = {
    greeting: "Greeting",
    "greeting-only": "Greeting only (player just said hello)",
    "how-can-i-help": "Nudge: how can I help you?",
    "escalation-notice": "Notice to player: handed over to an operator",
    "redirect-small-talk": "Redirect: back to the game question",
    "redirect-business": "Decline: business / partnership pitch",
    closing: "Closing: glad it helped",
    "feedback-thanks": "Thanks for feedback (no question), ticket → solved",
    "night-notice": "Night notice: support is offline",
    "restriction-cheater-hero-level": "Restriction notice: anticheat, hero levels (Hermes-confirmed)",
    "restriction-cheater-resources": "Restriction notice: anticheat, resources without purchases (Hermes-confirmed)",
    "restriction-chat-mute": "Restriction notice: chat mute until date (Hermes-confirmed)",
    "payment-review": "Notice to player: purchase checked, a specialist will look into it",
    "payment-not-found": "Notice to player: payment not found, handed over to an operator"
}

/** DialogMessage.decision / DialogAnswer.decision -> operator wording */
export const DECISION_LABEL = {
    SKIP: "skipped",
    GREET: "greeting",
    NUDGE_GREETING: "nudge after repeated greeting",
    ANSWER: "answer from knowledge base",
    ASK_DETAILS: "asked for details (no article)",
    REDIRECT_SMALL_TALK: "redirected small talk",
    REDIRECT_BUSINESS: "declined business pitch",
    SILENCE: "bot stays silent",
    CLOSE: "closing, ticket → solved",
    FEEDBACK_THANKS: "feedback without a question: thanks, ticket → solved",
    NIGHT: "night notice",
    ESCALATE_AGENT_REQUESTED: "escalated: player asked for a human",
    ESCALATE_CRITICAL: "escalated: critical category",
    ESCALATE_NOT_RESOLVED: "escalated: answer did not help",
    ESCALATE_NO_KNOWLEDGE: "escalated: no article covers it",
    ESCALATE_TURN_LIMIT: "escalated: bot turn limit",
    ESCALATE_NO_GAME_TAG: "escalated: no game tag",
    INVESTIGATE_RESTRICTION: "checking restrictions in Hermes",
    RESTRICTION_ANSWER: "restriction confirmed by Hermes, template reply",
    ESCALATE_NEEDS_PLAYER_ID: "escalated: restriction question, no player id in ticket",
    ESCALATE_INVESTIGATION_FAILED: "escalated: Hermes check failed",
    ESCALATE_RESTRICTION_REVIEW: "escalated: Hermes data needs an operator's look",
    INVESTIGATE_PROGRESS: "checking player progress in Hermes",
    ANSWER_EVIDENCE: "answer from Hermes facts + knowledge base",
    ESCALATE_PROGRESS_REVIEW: "escalated: progress checked in Hermes, for operator review",
    INVESTIGATE_PAYMENT: "checking the purchase in Hermes",
    ESCALATE_PAYMENT_REVIEW: "escalated: purchase checked in Hermes, for operator review",
    ESCALATE_PAYMENT_NOT_FOUND: "escalated: payment not found in Hermes even after details"
}
export const decisionLabel = (decision) => (decision ? DECISION_LABEL[decision] ?? decision.toLowerCase() : null)

export const ruleKeyLabel = (ruleKey) => (ruleKey ? RULE_KEY_LABEL[ruleKey] ?? ruleKey : null)

/** Card title: makes the difference between a draft and a message that really went out explicit */
export const answerTitle = (answer) => {
    const author = answerAuthor(answer)
    switch (answer?.status) {
        case "PENDING_APPROVAL": return `${author} draft · needs your decision`
        case "APPROVED": return `${author} answer · approved, not sent yet`
        case "SENT": return `${author} answer · sent to player`
        case "REJECTED": return `${author} draft · rejected, not sent`
        case "SUPERSEDED": return `${author} draft · outdated, replaced by a newer one`
        case "SEND_FAILED": return `${author} answer · approved, but sending failed`
        default: return `${author} answer · ${ANSWER_STATUS_LABEL[answer?.status] ?? answer?.status}`
    }
}

/**
 * Operator's copy of the draft next to the player's language: Russian (textRu); records made
 * before textRu existed fall back to the English version. Null when it would repeat the text.
 */
export const reviewVersion = (answer) => {
    if (answer?.textRu) {
        return answer.textRu !== answer.text ? {label: "Russian version", text: answer.textRu} : null
    }
    return answer?.textEn && answer.language && answer.language.toLowerCase() !== "en" && answer.textEn !== answer.text
        ? {label: "English version", text: answer.textEn}
        : null
}

/**
 * Several answers may share one sourceMessageId — only older records (sequence 0 = separate greeting,
 * 1 = main answer); new drafts are one message per player message with the greeting built in (withGreeting).
 * They must be approved in sequence order: each approve sends exactly one message.
 * Returns id -> {step, total, blockedBy} where blockedBy is the pending answer that has to go first.
 */
export const buildAnswerContext = (answers = []) => {
    const groups = new Map()
    for (const answer of answers) {
        if (answer.type !== "ANSWER") continue
        const key = answer.sourceMessageId ?? answer.id
        if (!groups.has(key)) groups.set(key, [])
        groups.get(key).push(answer)
    }
    const context = {}
    for (const group of groups.values()) {
        group.sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
        const firstUnsent = group.find(isUnsentAnswer)
        group.forEach((answer, index) => {
            context[answer.id] = {
                step: index + 1,
                total: group.length,
                blockedBy: isUnsentAnswer(answer) && firstUnsent && firstUnsent.id !== answer.id ? firstUnsent : null
            }
        })
    }
    return context
}

/** Number of player messages that still have at least one draft waiting (stale drafts pile up per message) */
export const pendingSourceCount = (answers = []) =>
    new Set(answers.filter(isPendingAnswer).map((answer) => answer.sourceMessageId ?? answer.id)).size

export const ANSWER_HINT = {
    PENDING_APPROVAL: "Prepared by the bot, NOT sent yet. Approve to send this text to the player, or reject it.",
    APPROVED: "Approved by an operator but NOT sent yet. Send to deliver this text to the player, or reject it.",
    SENT: "This text was delivered to the player in Zendesk.",
    REJECTED: "Rejected by an operator. The player never saw this text.",
    SUPERSEDED: "The player wrote again before this draft was approved; the newer draft covers the whole context. Nothing to do here.",
    SEND_FAILED: "Approved, but Zendesk delivery failed. Nothing to retry here — ai-zendesk team investigates."
}

export const CRITICAL_CATEGORIES = new Set(["PAYMENT", "ACCOUNT", "PROGRESS", "TRANSFER_PROGRESS", "SECURITY", "PRIVACY", "TECHNICAL"])

export const isPendingAnswer = (answer) => answer?.type === "ANSWER" && answer?.status === "PENDING_APPROVAL"

/** Approved-only answers are still unsent: they block later steps and can still be sent or rejected */
export const isUnsentAnswer = (answer) =>
    answer?.type === "ANSWER" && (answer?.status === "PENDING_APPROVAL" || answer?.status === "APPROVED")

const toDate = (value) => {
    if (!value) return null
    const date = typeof value === "string" ? parseISO(value) : new Date(value)
    return Number.isNaN(date.getTime()) ? null : date
}

export const formatRelative = (value) => {
    const date = toDate(value)
    return date ? formatDistanceToNowStrict(date, {addSuffix: true}) : ""
}

export const formatExact = (value) => {
    const date = toDate(value)
    return date ? format(date, "dd.MM.yyyy HH:mm:ss") : ""
}

export const formatTime = (value) => {
    const date = toDate(value)
    return date ? format(date, "dd.MM HH:mm") : ""
}

/**
 * Messages and answers merged into one chronological feed. Consecutive SYSTEM messages
 * are grouped so Zendesk triggers/forms don't drown the conversation.
 */
export const buildTimeline = (messages = [], answers = []) => {
    // a SENT answer is echoed into history as a BOT message with sourceId "answer:<id>";
    // the card already says "sent to player", so the echo would only duplicate the text
    const sentAnswerIds = new Set(answers.map((answer) => `answer:${answer.id}`))
    const visibleMessages = messages.filter((message) => !(message.role === "BOT" && sentAnswerIds.has(message.sourceId)))

    // answers for one player message are kept together and ordered by sequence (greeting first)
    const groupAt = new Map()
    for (const answer of answers) {
        const key = answer.sourceMessageId ?? answer.id
        const at = toDate(answer.createdAt)?.getTime() ?? 0
        groupAt.set(key, Math.min(groupAt.get(key) ?? Infinity, at))
    }

    const items = [
        ...visibleMessages.map((message) => ({kind: "message", at: message.receivedAt ?? message.occurredAt, data: message, seq: 0})),
        ...answers.map((answer) => ({
            kind: "answer",
            at: new Date(groupAt.get(answer.sourceMessageId ?? answer.id)).toISOString(),
            data: answer,
            seq: answer.sequence ?? 0
        }))
    ]
    items.sort((a, b) => {
        const diff = (toDate(a.at)?.getTime() ?? 0) - (toDate(b.at)?.getTime() ?? 0)
        if (diff !== 0) return diff
        // an answer created at the same instant as its source message goes after it
        const kind = (a.kind === "answer") - (b.kind === "answer")
        return kind !== 0 ? kind : a.seq - b.seq
    })

    const grouped = []
    for (const item of items) {
        const isSystem = item.kind === "message" && item.data.role === "SYSTEM"
        const last = grouped[grouped.length - 1]
        if (isSystem && last?.kind === "system-group") {
            last.items.push(item.data)
        } else if (isSystem) {
            grouped.push({kind: "system-group", at: item.at, items: [item.data]})
        } else {
            grouped.push(item)
        }
    }
    return grouped
}

/** Error body from the backend proxy is either ai-zendesk's {code,error} or plain text */
export const errorText = (error) => {
    const data = error?.data
    if (!data) return error?.status ? `Request failed (${error.status})` : "Request failed"
    if (typeof data === "string") return data
    return data.error ?? data.message ?? JSON.stringify(data)
}
